import { useEffect, useId, useState } from 'react';
import { aiApi } from '../../api/ai';
import { Button } from '../ui/Button';
import {
  describeAssistFailure,
  type AssistSnapshot,
  type ContentAssistAction,
  type ContentAssistInput,
  type ContentAssistResponse,
  type ContentAssistTone,
} from './contentAssist';

interface MakeWithAIDialogProps {
  open: boolean;
  snapshot: AssistSnapshot | null;
  metadata?: ContentAssistInput['metadata'];
  onApply: (replacement: string) => void;
  onClose: () => void;
  assist?: (input: ContentAssistInput) => Promise<ContentAssistResponse>;
}

const actions: { value: ContentAssistAction; label: string }[] = [
  { value: 'generate', label: 'Generate' },
  { value: 'rewrite', label: 'Rewrite' },
  { value: 'improve', label: 'Improve' },
  { value: 'shorten', label: 'Shorten' },
];

const tones: { value: ContentAssistTone; label: string }[] = [
  { value: 'professional', label: 'Professional' },
  { value: 'urgent', label: 'Urgent' },
  { value: 'neutral', label: 'Neutral' },
  { value: 'friendly', label: 'Friendly' },
];

const previewPolicy = `<meta http-equiv="Content-Security-Policy" content="default-src 'none'; script-src 'none'; style-src 'unsafe-inline' https: http:; img-src data: blob: https: http:; font-src data: https: http:; media-src data: https: http:; form-action 'none'; base-uri 'none';">`;

function previewDocument(target: AssistSnapshot['target'], replacement: string): string {
  if (target === 'email_html' || target === 'landing_html') return `${previewPolicy}${replacement}`;
  const text = replacement.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  return `${previewPolicy}<pre style="margin:16px;white-space:pre-wrap;font-family:sans-serif">${text}</pre>`;
}

export function MakeWithAIDialog({ open, snapshot, ...props }: MakeWithAIDialogProps) {
  if (!open || !snapshot) return null;
  const sessionKey = `${snapshot.target}:${snapshot.scope}:${snapshot.selectionStart}:${snapshot.selectionEnd}:${snapshot.fieldValue.length}`;
  return <AssistSession key={sessionKey} snapshot={snapshot} {...props} />;
}

function AssistSession({
  snapshot,
  metadata,
  onApply,
  onClose,
  assist = async (input) => (await aiApi.contentAssist(input)).data,
}: Omit<MakeWithAIDialogProps, 'open'> & { snapshot: AssistSnapshot }) {
  const titleId = useId();
  const [action, setAction] = useState<ContentAssistAction>('generate');
  const [tone, setTone] = useState<ContentAssistTone>('professional');
  const [instruction, setInstruction] = useState('');
  const [replacement, setReplacement] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [error, setError] = useState<{ message: string; unconfigured: boolean } | null>(null);
  const [pending, setPending] = useState(false);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      event.stopImmediatePropagation();
      onClose();
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [onClose]);

  const request = async () => {
    setPending(true);
    setError(null);
    try {
      const result = await assist({
        target: snapshot.target,
        action,
        tone,
        instruction: instruction.slice(0, 2000),
        selected_text: snapshot.selectedText,
        before_text: snapshot.beforeText.slice(0, 4000),
        after_text: snapshot.afterText.slice(0, 4000),
        document_html: snapshot.documentHtml.slice(0, 100000),
        document_text: snapshot.documentText.slice(0, 100000),
        metadata: metadata ?? snapshot.metadata,
      });
      if (!result.replacement.trim()) {
        setReplacement('');
        setError({ message: 'The model returned an empty result', unconfigured: false });
        return;
      }
      setReplacement(result.replacement);
    } catch (err) {
      setReplacement('');
      setError(describeAssistFailure(err));
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div role="dialog" aria-modal="true" aria-labelledby={titleId} className="relative mx-4 flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-xl bg-white p-6 shadow-xl">
        <h2 id={titleId} className="text-lg font-semibold text-gray-900">Make with AI</h2>
        <p className="mt-2 text-sm font-medium text-gray-800">{snapshot.label}</p>
        <p className="text-sm text-gray-500">{snapshot.scope === 'selection' ? 'Replaces the selection' : 'Replaces the whole field'}</p>
        <div className="mt-4 min-h-0 flex-1 space-y-3 overflow-auto">
          <label className="block text-sm font-medium text-gray-700" htmlFor="content-assist-action">Action</label>
          <select id="content-assist-action" className="w-full rounded-lg border border-gray-300 px-3 py-2" value={action} onChange={(event) => setAction(event.target.value as ContentAssistAction)}>
            {actions.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
          </select>
          <label className="block text-sm font-medium text-gray-700" htmlFor="content-assist-tone">Tone</label>
          <select id="content-assist-tone" className="w-full rounded-lg border border-gray-300 px-3 py-2" value={tone} onChange={(event) => setTone(event.target.value as ContentAssistTone)}>
            {tones.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
          </select>
          <label className="block text-sm font-medium text-gray-700" htmlFor="content-assist-instruction">Custom instruction</label>
          <textarea id="content-assist-instruction" className="min-h-20 w-full rounded-lg border border-gray-300 px-3 py-2" value={instruction} onChange={(event) => setInstruction(event.target.value)} />
          {replacement && (
            <div>
              <div className="flex items-center justify-between gap-3">
                <label className="block text-sm font-medium text-gray-700" htmlFor="content-assist-result">Proposed replacement</label>
                <Button type="button" variant="secondary" size="sm" onClick={() => setShowPreview((open) => !open)}>{showPreview ? 'Hide preview' : 'Preview'}</Button>
              </div>
              <textarea id="content-assist-result" readOnly className="mt-1 min-h-28 w-full rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 font-mono text-sm" value={replacement} />
              {showPreview && (
                <iframe
                  title="Proposed replacement preview"
                  sandbox=""
                  srcDoc={previewDocument(snapshot.target, replacement)}
                  className="mt-3 h-80 w-full rounded-lg border border-gray-200 bg-white"
                />
              )}
            </div>
          )}
          {error && <p role="alert" className="text-sm text-red-700">{error.message}</p>}
          {error?.unconfigured && <a className="text-sm text-indigo-700 underline" href="/settings">Settings</a>}
        </div>
        {pending && <p className="mt-4 text-sm text-gray-500">A full template can take a couple of minutes.</p>}
        <div className="mt-5 flex justify-end gap-3">
          <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
          {error && <Button type="button" variant="secondary" loading={pending} onClick={() => void request()}>Retry</Button>}
          <Button type="button" loading={pending} onClick={() => void request()}>Generate</Button>
          <Button type="button" disabled={!replacement} onClick={() => onApply(replacement)}>Apply</Button>
        </div>
      </div>
    </div>
  );
}
